import './compact-legend.css';

import h from 'hyperscript';
import Control from 'ol/control/Control';
import { unByKey } from 'ol/Observable';
import legendIcon from '@disy/cadenza-icons/legend.svg';

import { createIconButton } from 'ui/button/button';

import { LayerProperty } from 'cadenza/map/layer-property';
import { GraphicalLegendPart } from 'cadenza/map/legend/graphical-legend-part';
import {
  getLayerTasks,
  LAYER_EVENT_ALL_TASKS_COMPLETED,
  LAYER_EVENT_TASKS_CANCELED,
  LAYER_EVENT_TASKS_ONGOING
} from 'cadenza/map/layer-tasks';
import { currentWindowMediaXsOnly } from 'cadenza/utils/media';
import { context } from 'cadenza/map/context';
import { icon } from 'cadenza/utils/icon/icon';
import {
  BlockIdentifier,
  BlockVisibilityValues,
  getBlockSettingsSingleton
} from 'cadenza/map/block-settings';
import { FeatureName, isFeatureAvailable } from 'cadenza/features';
import { debounce } from 'cadenza/utils/debounce';

import i18n from './messages.properties';

export class CompactLegend extends Control {

  #buttonContainer;
  #mainContainer;
  #openListeners = [];
  #eventKeys;
  #blockSettings;
  #isCollapsedByDefault;
  _overlayTarget;
  _debouncedRefresh;

  constructor (target, { overlayTarget, isCollapsedByDefault = true } = {}) {
    const toggleButton = createIconButton(icon(legendIcon), i18n('compactLegend.button.tooltip'), {
      size: 's',
      styleClass: 'button-invert',
      onclick: _ => this.toggle()
    });
    const buttonContainer = h('.d-compact-legend--button-container', toggleButton);
    super({ element: buttonContainer, target });
    this._overlayTarget = overlayTarget;
    this.#buttonContainer = buttonContainer;
    this.#blockSettings = getBlockSettingsSingleton();
    target.dataset.testid = 'map-compact-legend';
    this.#isCollapsedByDefault = isCollapsedByDefault;
    this._debouncedRefresh = debounce(collapsing => this._refresh(collapsing));
  }

  setMap (map) {
    super.setMap(map);
    this.#mainContainer = h('aside.d-compact-legend--main-container');
    setTimeout(() => this._positionMainContainer());
    window.addEventListener('resize', () => this._positionMainContainer());
    if (this._overlayTarget) {
      this._overlayTarget.append(this.#mainContainer);
    } else {
      const layerMenu = new Control({
        element: this.#mainContainer
      });
      map.addControl(layerMenu);
    }
    this.setCollapsedWithoutNotifications(this.#isCollapsedByDefault);
    this._refresh(this.getCollapsed());
    this._createChangeListeners(map);
  }

  addOpenListener (listener) {
    this.#openListeners.push(listener);
  }

  _createChangeListeners (map) {
    const layers = map.getLayers();
    layers.forEach(layer => this._addLayerListeners(layer, layers));
    this.#eventKeys = [
      layers.on('change', () => this._debouncedRefresh()),
      layers.on('add', ({ element: layer }) => this._addLayerListeners(layer, layers)),
      layers.on('remove', () => this._debouncedRefresh())
    ];
    this.#blockSettings.on(`change:${BlockIdentifier.COMPACT_LEGEND}`, () => {
      this.setCollapsedWithoutNotifications(!this.#blockSettings.isBlockVisible(BlockIdentifier.COMPACT_LEGEND));
    });
    map.on('moveend', () => layers.forEach(layer => this._refreshInRange(map, layer)));
  }

  _addLayerListeners (layer, layers) {
    const eventKeys = layer.on([ 'change:name', 'change:visible' ], () => this._debouncedRefresh());
    eventKeys.push(layer.getSource().on('change', () => this._debouncedRefresh()));

    // Remove listeners on removal of layer for garbage collection
    layers.on('remove', ({ element: removedLayer }) => {
      if (removedLayer === layer) {
        unByKey(eventKeys);
      }
    });
  }

  _refreshInRange (map, layer) {
    const legendLayerElement = this.#mainContainer.querySelector(`[data-layer-id='${layer.get(LayerProperty.ID)}']`);
    if (!legendLayerElement) {
      return;
    }
    const inRange = context(layer).getInRange(map.getView().getResolution());
    legendLayerElement.classList.toggle('is-in-range', inRange);
  }

  _refreshHasEmptyRequiredCondition (layer) {
    const legendLayerElement = this.#mainContainer.querySelector(`[data-layer-id='${layer.get(LayerProperty.ID)}']`);
    if (!legendLayerElement) {
      return;
    }
    const hasEmptyRequiredCondition = layer.get(LayerProperty.HAS_EMPTY_REQUIRED_CONDITIONS);
    legendLayerElement.classList.toggle('has-empty-required-condition', hasEmptyRequiredCondition);
  }

  /**
   * The height of the map controls bar is not always predictable, even for a given screen size
   * because of possible line breaks in the elements. Thus the positioning and sizing of the legend
   * has to be adjusted by JavaScript on resize.
   *
   * @private
   */
  _positionMainContainer () {
    const mapControlsBar = this.#buttonContainer.closest('.d-map-controls');
    const mapToolbar = this.#buttonContainer.closest('.mapContainer').querySelector('d-legacy-map-toolbar');
    const bottomMargin = currentWindowMediaXsOnly.matches ? 0 : 10;
    const bottom = mapControlsBar.clientHeight + bottomMargin;
    const mapToolbarHeight = mapToolbar ? mapToolbar.clientHeight : 0;
    if (this.#mainContainer) {
      this.#mainContainer.style.bottom = !this.getCollapsed() ? bottom + 'px' : null;
      this.#mainContainer.style.maxHeight = `calc((100% - ${bottom + mapToolbarHeight}px)/2)`;
    }
  }

  /**
   * Recreate the contents of the compact legend on changes (eg layer added).
   *
   * @param {boolean} collapsing - indicates if true that the compact legend is getting minimized
   * @private
   */
  _refresh (collapsing = false) {
    this.#mainContainer.innerHTML = '';
    // When the compact legend is getting collapsed the graphical legend parts
    // are not added at all as they would not be shown anyways.
    // The graphical legend parts would initiate useless expensive get legend requests
    // to the server for legend images on each map interaction (eg zoom) if added.
    if (collapsing) {
      return;
    }

    const mapLegend = document.querySelector('d-map-legend');
    const map = this.getMap();
    let compactLegendLayers;
    if (mapLegend) {
      const visibleLegendLayers = mapLegend.visibleLegendLayers();
      const visibleLegendLayersWithVisibleLayers = visibleLegendLayers.filter(legendLayer => legendLayer.layer.getVisible());
      compactLegendLayers = visibleLegendLayersWithVisibleLayers.map(
        legendLayer => this._createCompactLegendLayer(legendLayer.layer, legendLayer.querySelector('d-graphical-legend-part')));
    } else {
      const visibleLayersVisibleInLayerList = map.getLayers().getArray()
        .filter(layer => layer.get(LayerProperty.VISIBLE_IN_LAYERLIST) && layer.getVisible())
        .filter(layer => {
          const groupThemeChoices = layer.get(LayerProperty.NORMAL_GROUP_CHOICES);
          return !groupThemeChoices || !!groupThemeChoices.find(choice => choice.visible === true);
        })
        .reverse();
      compactLegendLayers = visibleLayersVisibleInLayerList.map(legendLayer => this._createCompactLegendLayer(legendLayer));
    }
    if (compactLegendLayers.length > 0) {
      this.#mainContainer.append(...compactLegendLayers);
    } else {
      this.#mainContainer.append(i18n('title'));
    }
    map.getLayers().forEach(layer => {
      this._refreshInRange(map, layer);
      this._refreshHasEmptyRequiredCondition(layer);
    });
  }

  _createCompactLegendLayer (layer, masterGraphicalLegendPart = null) {
    const layerTasks = getLayerTasks(layer);
    const progressSpinner = h('.d-compact-legend--spinner-container', h('d-progress-spinner.d-progress-spinner-xs.d-progress-spinner-gray'));
    progressSpinner.hidden = true;
    const graphicalLegendPart = new GraphicalLegendPart(layer, this.getMap(), { master: masterGraphicalLegendPart, isClientSideRendered: isFeatureAvailable(FeatureName.CLIENT_SIDE_RENDERED_LEGEND)  });
    const ongoingTasks = layerTasks.hasOngoingTasks();
    progressSpinner.hidden = !ongoingTasks;
    graphicalLegendPart.hidden = ongoingTasks;
    layer.on(LAYER_EVENT_TASKS_ONGOING, () => {
      progressSpinner.hidden = false;
      graphicalLegendPart.hidden = true;
    });
    layer.on(LAYER_EVENT_ALL_TASKS_COMPLETED, () => {
      progressSpinner.hidden = true;
      graphicalLegendPart.hidden = false;
      // prevent size change when the loading spinner replaces the image the next time
      progressSpinner.style.minHeight = graphicalLegendPart.offsetHeight + 'px';
      progressSpinner.style.minWidth = graphicalLegendPart.offsetWidth + 'px';
    });
    layer.on(LAYER_EVENT_TASKS_CANCELED, () => {
      progressSpinner.hidden = true;
      graphicalLegendPart.hidden = true;
    });
    return (h('.d-compact-legend--layer', { 'data-layer-id': layer.get(LayerProperty.ID) }, [
      h('h2.d-compact-legend--layer-heading', layer.get(LayerProperty.NAME)),
      progressSpinner,
      graphicalLegendPart,
      h('.d-compact-legend--out-of-range-text', i18n('layer.outOfRange')),
      h('hr.d-compact-legend--separator')
    ]));
  }

  toggle () {
    this.setCollapsed(!this.getCollapsed());
  }

  setCollapsedWithoutNotifications (collapsed) {
    // There is an unwanted effect when the toggle button is used. The change listener
    // registered in _createChangeListeners above will fire after the toggle, resulting
    // in duplicate execution of _setCollapsed. The following statement prevents this.
    if (this.getCollapsed() === collapsed) {
      return;
    }
    this._setCollapsed(collapsed, true);
  }

  setCollapsed (collapsed) {
    this._setCollapsed(collapsed);
  }

  _setCollapsed (collapsed, suppressNotifications = false) {
    const collapsing = !this.getCollapsed() && collapsed;
    this._refresh(collapsing);
    if (collapsed && !this.#mainContainer.classList.contains('d-compact-legend--is-out-of-sight')) {
      this.#mainContainer.classList.toggle('d-compact-legend--is-out-of-sight', true);
      this.#mainContainer.tabIndex = -1; // prevents hidden legend from being keyboard-selectable in Firefox, see CADENZA-18037
      this._positionMainContainer();
    } else if (!collapsed && this.#mainContainer.classList.contains('d-compact-legend--is-out-of-sight')) {
      this.#mainContainer.classList.toggle('d-compact-legend--is-out-of-sight', false);
      this._positionMainContainer();
      this.#mainContainer.removeAttribute('tabindex');
      if (!suppressNotifications) {
        for (const listener of this.#openListeners) {
          listener();
        }
      }
    }
    if (!suppressNotifications) {
      this.#blockSettings.setVisibility(BlockIdentifier.COMPACT_LEGEND, collapsed ? BlockVisibilityValues.minimized : BlockVisibilityValues.regular);
    }
  }

  getCollapsed () {
    return this.#mainContainer.classList.contains('d-compact-legend--is-out-of-sight');
  }

  getLegendElement () {
    return this.#mainContainer;
  }

}
